import { Alert, CopyToClipboard, Button } from "@cloudscape-design/components";
import StatusCard from "../../components/StatusCard";
import styles from "./PaymentStatus.module.scss";
import { GetPaymentStatusResponse } from "../../../../common/models/payment_status";
import { getStatusCardStatus, StatusCardType } from '../StatusCard/utils'
import useInterval from "../../hooks/useInterval";

const PAYMENT_STATUS_POLL_INTERVAL_MS = 2000;

interface Props {
    paymentStatus?: GetPaymentStatusResponse,
    updatePaymentStatus: () => Promise<void>
    onReset: () => void
}

const PaymentStatus = ({paymentStatus, updatePaymentStatus, onReset}: Props) => {

    const updateStatusIfNotComplete = async () => {
        if (paymentStatus?.Status?.COMPLETE || paymentStatus?.Status?.ERROR) return
        await updatePaymentStatus()
    }

    useInterval(updateStatusIfNotComplete, PAYMENT_STATUS_POLL_INTERVAL_MS);

    const {
        Status,
        paymentData,
        refusalReason,
        success
    } = paymentStatus ?? {} as GetPaymentStatusResponse

    const pspReference = paymentData?.payment?.id

    const canReset = Status?.COMPLETE || Status?.ERROR

    return (
        <section className={styles.container}>
            {success ? (
                <Alert
                    type="success"
                    header="Payment has successfully completed"
                    className={styles.alertBanner}
                >
                    <figure role="alert">
                        {pspReference ? (
                            <div className={styles.paymentReference}>
                                <p>Payment Reference Number: {pspReference}</p>
                                <CopyToClipboard
                                copyButtonAriaLabel="Copy payment reference"
                                copyErrorText="Payment reference failed to copy"
                                copySuccessText="Payment refernece copied"
                                textToCopy={pspReference}
                                variant="icon"
                                />
                            </div>
                        ) : null}
                        <p>Payment Status: Complete</p>
                    </figure>
                </Alert>
            ) : null}

            {success === false || Status?.ERROR ? (
                <Alert
                    type="error"
                    header="Payment has not completed"
                    className={styles.alertBanner}
                >
                    <figure role="alert">
                        {refusalReason ? <p>Refusal Reason: {refusalReason}</p> : null}
                        <p>Payment Status: Failed</p>
                    </figure>
                </Alert>
            ) : null}

            <div className={styles.paymentStatusCards}>
                <StatusCard
                    title="Order confirmation"
                    status={getStatusCardStatus(StatusCardType.CUSTOMER_CONFIRMED, Status, success)}
                />
                <StatusCard
                    title="Card number"
                    status={getStatusCardStatus(StatusCardType.CARD_NUMBER, Status, success)}
                />
                <StatusCard
                    title="Expiry date"
                    status={getStatusCardStatus(StatusCardType.EXPIRY_DATE, Status, success)}
                />
                <StatusCard
                    title="CV2/CVV"
                    status={getStatusCardStatus(StatusCardType.CVV, Status, success)}
                />
                <StatusCard
                    title="Submitted to provider"
                    status={getStatusCardStatus(StatusCardType.SUBMITTED, Status, success)}
                />
            </div>

            <div className={styles.confirmButton}>
                <Button
                    variant="primary"
                    disabled={!canReset}
                    onClick={onReset}
                >
                    Reset order
                </Button>
            </div>
        </section>
    );
};

export default PaymentStatus;
